import { useRef, useState, useCallback, useEffect } from 'react';
import { convertHeicToJpeg, isHeicFile, isValidImageFile } from '../utils/heicConverter';
import { compressImage } from '../utils/imageCompression';

export interface CropRegion {
  /** Fraction from left (0-1) */
  x: number;
  /** Fraction from top (0-1) */
  y: number;
  /** Fraction of width (0-1) */
  width: number;
  /** Fraction of height (0-1) */
  height: number;
}

export interface UseMediaCaptureOptions {
  /** Maximum file size in MB before compression. Default: 10 */
  maxFileSizeMB?: number;
  /** Maximum image dimension (px) for compression. Default: 2048 */
  maxImageDimension?: number;
  /** JPEG quality for compression. Default: 0.85 */
  jpegQuality?: number;
  /** Camera facing mode. Default: 'environment' (back camera) */
  facingMode?: 'environment' | 'user';
}

export interface UseMediaCaptureReturn {
  // Camera
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  cameraActive: boolean;
  cameraError: string | null;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  capturePhoto: (cropRegion?: CropRegion) => Promise<File | null>;
  /** Use an externally-produced blob as the captured photo (e.g. perspective-warped). */
  acceptCapturedBlob: (blob: Blob, filename?: string) => Promise<File | null>;
  /** Trigger a single-shot focus at a point in the video (0-1 fractions). Returns true if applied. */
  focusAt: (x: number, y: number) => Promise<boolean>;
  /** Whether the active track supports torch */
  torchSupported: boolean;
  torchOn: boolean;
  toggleTorch: () => Promise<void>;
  /** Current zoom info, if supported */
  zoom: { min: number; max: number; step: number; value: number } | null;
  setZoom: (value: number) => Promise<void>;

  // File upload
  handleFileSelect: (file: File) => Promise<File | null>;

  // Preview
  previewUrl: string | null;
  previewFile: File | null;
  clearPreview: () => void;

  // State
  processing: boolean;
  processingMessage: string;
}

/**
 * Custom hook for camera capture and file upload with automatic
 * HEIC conversion, image compression, file size validation,
 * and proper resource cleanup (blob URLs, camera streams).
 */
export function useMediaCapture(options: UseMediaCaptureOptions = {}): UseMediaCaptureReturn {
  const {
    maxFileSizeMB = 10,
    maxImageDimension = 2048,
    jpegQuality = 0.85,
    facingMode = 'environment',
  } = options;

  const videoRef = useRef<HTMLVideoElement>(null!);
  const canvasRef = useRef<HTMLCanvasElement>(null!);
  const streamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoomState] = useState<{ min: number; max: number; step: number; value: number } | null>(null);

  // Cleanup blob URL
  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  // Set preview with proper URL management
  const setPreviewWithUrl = useCallback((file: File) => {
    revokePreviewUrl();
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewUrl(url);
    setPreviewFile(file);
  }, [revokePreviewUrl]);

  // Clear preview
  const clearPreview = useCallback(() => {
    revokePreviewUrl();
    setPreviewUrl(null);
    setPreviewFile(null);
  }, [revokePreviewUrl]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    videoTrackRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setTorchSupported(false);
    setTorchOn(false);
    setZoomState(null);
  }, []);

  // Pick the best rear-facing camera, skipping ultra-wide / telephoto / depth lenses.
  // Returns null if there's only one back camera (no choice to make) — in that
  // case the caller should keep the stream it already has rather than reopen.
  const pickBackCameraId = useCallback(async (currentId: string | undefined): Promise<string | null> => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return null;
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === 'videoinput' && d.label);
      if (cams.length === 0) return null;

      const isBack = (label: string) => /back|rear|environment|world/i.test(label);
      const isAvoid = (label: string) =>
        /ultra|wide angle|wide-angle|0\.5|telephoto|tele|zoom|depth|ir|infrared|truedepth|mono/i.test(label);

      const backCams = cams.filter(c => isBack(c.label));
      // Only attempt a switch if we actually have multiple back cameras to
      // choose from — single-cam devices (desktops, most laptops) shouldn't
      // pay the cost (and risk) of a second getUserMedia call.
      if (backCams.length < 2) return null;

      const preferred = backCams.find(c => !isAvoid(c.label)) ?? backCams[0];
      if (!preferred || preferred.deviceId === currentId) return null;
      return preferred.deviceId;
    } catch {
      return null;
    }
  }, []);

  // Apply post-stream track constraints: continuous focus, zoom to narrow FOV.
  // These can't be reliably set in getUserMedia constraints on all browsers, so we
  // apply them via applyConstraints after the stream is live.
  const tuneTrackForCard = useCallback(async (track: MediaStreamTrack) => {
    const caps: any = typeof track.getCapabilities === 'function' ? track.getCapabilities() : {};
    const advanced: any[] = [];

    if (caps.focusMode && Array.isArray(caps.focusMode)) {
      if (caps.focusMode.includes('continuous')) advanced.push({ focusMode: 'continuous' });
    }
    if (caps.exposureMode && Array.isArray(caps.exposureMode) && caps.exposureMode.includes('continuous')) {
      advanced.push({ exposureMode: 'continuous' });
    }
    if (caps.whiteBalanceMode && Array.isArray(caps.whiteBalanceMode) && caps.whiteBalanceMode.includes('continuous')) {
      advanced.push({ whiteBalanceMode: 'continuous' });
    }

    // Zoom: explicitly reset to 1x — some devices remember a previous zoom level
    // on the track, and anything above 1x forces the user to step back to fit
    // the card in frame.
    let zoomInfo: { min: number; max: number; step: number; value: number } | null = null;
    if (caps.zoom && typeof caps.zoom === 'object') {
      const min = caps.zoom.min ?? 1;
      const max = caps.zoom.max ?? 1;
      const step = caps.zoom.step ?? 0.1;
      const target = Math.min(Math.max(1, min), max);
      advanced.push({ zoom: target });
      zoomInfo = { min, max, step, value: target };
    }

    if (advanced.length > 0) {
      try {
        await track.applyConstraints({ advanced } as MediaTrackConstraints);
      } catch {
        /* best-effort */
      }
    }

    setTorchSupported(Boolean(caps.torch));
    setZoomState(zoomInfo);
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraError(null);

    // Check for camera support
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not supported on this device or browser.');
      return;
    }

    try {
      // Stop any existing stream first
      stopCamera();

      // First pass: open any environment-facing camera so we get a labelled
      // device list (labels are only populated after permission is granted).
      let stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
      });

      // If we asked for the back cam and the device exposes multiple back
      // cameras, try to upgrade to the *standard* back cam (skip ultra-wide
      // / telephoto). Stop the original stream FIRST — browsers may refuse
      // a second concurrent stream on the same physical device.
      if (facingMode === 'environment') {
        const currentId = stream.getVideoTracks()[0]?.getSettings().deviceId;
        const preferredId = await pickBackCameraId(currentId);
        if (preferredId) {
          stream.getTracks().forEach(t => t.stop());
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: { exact: preferredId },
                width: { ideal: 1920 },
                height: { ideal: 1440 },
              },
            });
          } catch {
            // Re-open the original facingMode stream as a fallback.
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: { ideal: facingMode },
                width: { ideal: 1920 },
                height: { ideal: 1440 },
              },
            });
          }
        }
      }

      streamRef.current = stream;
      const track = stream.getVideoTracks()[0];
      videoTrackRef.current = track ?? null;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          // Autoplay policies sometimes reject .play(); the stream is still live
          // and the <video> with playsinline+muted should resume on its own.
          // eslint-disable-next-line no-console
          console.warn('[camera] video.play() rejected, stream still attached:', playErr);
        }
      }

      setCameraActive(true);

      // Apply focus/zoom tuning — don't block UI on this.
      if (track) {
        void tuneTrackForCard(track);
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[camera] getUserMedia failed:', err?.name, err?.message, err);
      let message = `Failed to access camera (${err?.name || 'unknown error'}).`;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        message = 'Camera access denied. Please allow camera permission and try again.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        message = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        message = 'Camera is in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        message = 'Camera does not support the requested resolution.';
      } else if (err.name === 'SecurityError') {
        message = 'Camera blocked: the page must be served over HTTPS (or localhost).';
      }
      setCameraError(message);
      setCameraActive(false);
    }
  }, [facingMode, stopCamera, pickBackCameraId, tuneTrackForCard]);

  // Wrap an externally-produced blob (e.g. a perspective-corrected card image)
  // into the same preview/state flow as capturePhoto.
  const acceptCapturedBlob = useCallback(async (blob: Blob, filename?: string): Promise<File | null> => {
    const file = new File(
      [blob],
      filename ?? `business-card-${Date.now()}.jpg`,
      { type: blob.type || 'image/jpeg', lastModified: Date.now() }
    );
    setPreviewWithUrl(file);
    stopCamera();
    return file;
  }, [setPreviewWithUrl, stopCamera]);

  // Tap-to-focus: request a single-shot autofocus at a point (0-1 fractions).
  const focusAt = useCallback(async (x: number, y: number): Promise<boolean> => {
    const track = videoTrackRef.current;
    if (!track || typeof track.getCapabilities !== 'function') return false;
    const caps: any = track.getCapabilities();
    const advanced: any[] = [];
    if (Array.isArray(caps.focusMode) && caps.focusMode.includes('single-shot')) {
      advanced.push({ focusMode: 'single-shot' });
    } else if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
      advanced.push({ focusMode: 'continuous' });
    }
    if (caps.pointsOfInterest) {
      advanced.push({ pointsOfInterest: [{ x, y }] });
    }
    if (Array.isArray(caps.exposureMode) && caps.exposureMode.includes('single-shot')) {
      advanced.push({ exposureMode: 'single-shot' });
    }
    if (advanced.length === 0) return false;
    try {
      await track.applyConstraints({ advanced } as MediaTrackConstraints);
      return true;
    } catch {
      return false;
    }
  }, []);

  const toggleTorch = useCallback(async () => {
    const track = videoTrackRef.current;
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next } as any] });
      setTorchOn(next);
    } catch {
      /* ignore */
    }
  }, [torchOn]);

  const setZoom = useCallback(async (value: number) => {
    const track = videoTrackRef.current;
    if (!track || !zoom) return;
    const clamped = Math.min(zoom.max, Math.max(zoom.min, value));
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped } as any] });
      setZoomState({ ...zoom, value: clamped });
    } catch {
      /* ignore */
    }
  }, [zoom]);

  // Capture photo from camera, optionally cropping to a region
  const capturePhoto = useCallback(async (cropRegion?: CropRegion): Promise<File | null> => {
    if (!videoRef.current || !canvasRef.current || !cameraActive) {
      return null;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (cropRegion) {
        // Crop to the specified region of the video feed
        const srcX = Math.round(video.videoWidth * cropRegion.x);
        const srcY = Math.round(video.videoHeight * cropRegion.y);
        const srcW = Math.round(video.videoWidth * cropRegion.width);
        const srcH = Math.round(video.videoHeight * cropRegion.height);

        canvas.width = srcW;
        canvas.height = srcH;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
      } else {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(video, 0, 0);
      }

      return new Promise<File | null>((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            const file = new File([blob], `business-card-${Date.now()}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            setPreviewWithUrl(file);
            stopCamera();
            resolve(file);
          },
          'image/jpeg',
          jpegQuality
        );
      });
    } catch {
      return null;
    }
  }, [cameraActive, jpegQuality, setPreviewWithUrl, stopCamera]);

  // Process a file (validate, convert HEIC, compress)
  const processFile = useCallback(async (file: File): Promise<File | null> => {
    // Validate file type
    if (!isValidImageFile(file)) {
      throw new Error('Invalid image type. Only JPEG, PNG, WebP, and HEIC are supported.');
    }

    // Validate file size (before any processing)
    const maxBytes = maxFileSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(
        `Image is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is ${maxFileSizeMB}MB.`
      );
    }

    let processedFile = file;

    // Convert HEIC to JPEG
    if (isHeicFile(file)) {
      setProcessingMessage('Converting image format...');
      processedFile = await convertHeicToJpeg(file);
    }

    // Compress/resize if needed
    setProcessingMessage('Optimizing image...');
    processedFile = await compressImage(processedFile, {
      maxDimension: maxImageDimension,
      quality: jpegQuality,
    });

    return processedFile;
  }, [maxFileSizeMB, maxImageDimension, jpegQuality]);

  // Handle file selection (from upload input)
  const handleFileSelect = useCallback(async (file: File): Promise<File | null> => {
    setProcessing(true);
    setProcessingMessage('Processing image...');
    setCameraError(null);

    try {
      const processedFile = await processFile(file);
      if (processedFile) {
        setPreviewWithUrl(processedFile);
      }
      return processedFile;
    } catch (err: any) {
      setCameraError(err.message || 'Failed to process image.');
      return null;
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  }, [processFile, setPreviewWithUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      revokePreviewUrl();
    };
  }, [stopCamera, revokePreviewUrl]);

  return {
    videoRef,
    canvasRef,
    cameraActive,
    cameraError,
    startCamera,
    stopCamera,
    capturePhoto,
    acceptCapturedBlob,
    focusAt,
    torchSupported,
    torchOn,
    toggleTorch,
    zoom,
    setZoom,
    handleFileSelect,
    previewUrl,
    previewFile,
    clearPreview,
    processing,
    processingMessage,
  };
}
